
import React from 'react';
import { nodeTypeConfigurations } from './NodeRegistry';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';

interface FlowSidebarProps {
  onDragStart: (event: React.DragEvent<HTMLDivElement>, nodeType: string) => void;
  onSaveFlow: () => void;
  onClearFlow: () => void;
  isFlowEmpty: boolean;
  isFlowModified: boolean;
  isSaving?: boolean;
}

export const FlowSidebar: React.FC<FlowSidebarProps> = ({
  onDragStart,
  onSaveFlow,
  onClearFlow,
  isFlowEmpty,
  isFlowModified,
  isSaving = false
}) => {
  const { currentUser } = useAuth();
  const companyId = currentUser?.companyId || localStorage.getItem('currentCompanyId');
  
  return (
    <div className="w-64 border-r h-full bg-card flex flex-col">
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold mb-1">Construtor de Fluxo</h2>
        <p className="text-xs text-muted-foreground">
          Arraste os componentes para criar seu fluxo de automação
        </p>
      </div>
      
      <Tabs defaultValue="nodes" className="flex-1 flex flex-col">
        <TabsList className="w-full justify-start px-4 pt-2">
          <TabsTrigger value="nodes">Componentes</TabsTrigger>
          <TabsTrigger value="flows">Meus Fluxos</TabsTrigger>
        </TabsList>
        
        <TabsContent value="nodes" className="flex-1 flex flex-col">
          <ScrollArea className="flex-1">
            <div className="p-4 space-y-4">
              {nodeTypeConfigurations.map((nodeConfig) => (
                <div
                  key={nodeConfig.type}
                  className={`p-3 rounded-md border cursor-grab ${nodeConfig.color} bg-opacity-20`}
                  draggable
                  onDragStart={(event) => onDragStart(event, nodeConfig.type)}
                >
                  <div className="flex items-center gap-2">
                    {nodeConfig.icon}
                    <div>
                      <h3 className="text-sm font-medium">{nodeConfig.label}</h3>
                      <p className="text-xs text-muted-foreground">
                        {nodeConfig.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          
          <div className="p-4 border-t space-y-2">
            <Button 
              onClick={onSaveFlow} 
              className="w-full" 
              disabled={isFlowEmpty || !isFlowModified || isSaving}
            >
              {isSaving ? "Salvando..." : "Salvar Fluxo"}
            </Button>
            <Button 
              variant="outline" 
              onClick={onClearFlow} 
              className="w-full" 
              disabled={isFlowEmpty || isSaving}
            >
              Limpar Tudo
            </Button>
          </div>
        </TabsContent>
        
        <TabsContent value="flows" className="flex-1">
          <ScrollArea className="h-full">
            <div className="p-4">
              <p className="text-sm text-muted-foreground mb-4">
                Seus fluxos salvos aparecem aqui
              </p>
              
              {/* Flow list will be populated dynamically */}
              <div className="text-xs text-center text-muted-foreground py-8">
                Nenhum fluxo salvo ainda
              </div>
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
};
